import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, update, get } from 'firebase/database';
import { auth, db } from './firebase';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';
const COMMISSION_RATE = 0.30;
const SERVICE_FEE = 5; // flat GH₵5 per order, matches the customer app — goes entirely to PharmDev

const inp = {
  width: '100%', padding: '11px 14px', borderRadius: 10,
  border: '1px solid #E5E7EB', fontSize: 14,
  backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box',
};

const EMPTY_FORM = {
  customerName:  '',
  customerPhone: '',
  momoNumber:    '',
  momoName:      '',
  address:       '',
  ghanaPostGPS:  '',
  notes:         '',
  pharmacyId:    '',
  pharmacyName:  '',
  pharmacyPhone: '',
  deliveryFee:   '12',
};

// orders/users come from App.js, which already holds one live subscription
// to each — re-subscribing/re-scanning here duplicated the full system-wide
// trees on every lookup click and every mount.
export default function PhoneOrderTab({ pharmacies, orders = [], users = [] }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [placing, setPlacing]   = useState(false);
  const [pollStatus, setPollStatus] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [success, setSuccess]   = useState(null);

  // ── ASSISTED CUSTOMER LOOKUP ─────────────────────────────────────────────
  // Many callers (often elderly customers who can't navigate the app
  // themselves) already have an account or have ordered before. Looking
  // them up means: (1) staff doesn't need to re-collect info that's
  // already on file, and (2) the order is tied to their real account so
  // it actually shows up in their own app's order history, instead of
  // being invisible to them forever under a throwaway phone_ id.
  const [lookingUp, setLookingUp]         = useState(false);
  const [customerMatch, setCustomerMatch] = useState(null); // real registered account, if found
  const [addressSuggestion, setAddressSuggestion] = useState(null); // from a past order with this phone

  const lookupCustomer = async () => {
    const phone = form.customerPhone.trim();
    if (!phone) { alert('Enter the phone number first'); return; }
    setLookingUp(true);
    setCustomerMatch(null);
    setAddressSuggestion(null);
    try {
      // Both users and orders are already resident in App.js's own state
      // (passed down as props) — scanning them in-memory here costs zero
      // extra network bytes, versus a fresh full-tree download per lookup.
      const found = users.find(u => u.phone === phone) || null;
      if (found) {
        setCustomerMatch(found);
        setForm(f => ({ ...f, customerName: found.name || f.customerName }));
      }

      const mostRecentAddress = orders
        .filter(o => o.customerPhone === phone && o.deliveryAddress)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      if (mostRecentAddress) {
        setAddressSuggestion({ address: mostRecentAddress.deliveryAddress, ghanaPostGPS: mostRecentAddress.ghanaPostGPS || '' });
      }

      if (!found && !mostRecentAddress) {
        alert('No account or past order found for this number — this looks like a new caller.');
      }
    } catch (e) {
      alert('Lookup failed: ' + e.message);
    } finally { setLookingUp(false); }
  };

  const applySuggestedAddress = () => {
    if (!addressSuggestion) return;
    setForm(f => ({ ...f, address: addressSuggestion.address, ghanaPostGPS: addressSuggestion.ghanaPostGPS }));
  };

  // ── MEDICINE CATALOG SEARCH (replaces free-text price entry) ────────────
  // Staff search the pharmacy's real, priced inventory instead of typing
  // "Name, Price" from memory — this is where GH₵0 phone orders and typo'd
  // prices used to come from. A custom-item fallback still exists for
  // anything genuinely not in the catalog.
  const [catalog, setCatalog]             = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [cartItems, setCartItems]         = useState([]);
  const [showCustomItem, setShowCustomItem] = useState(false);
  const [customItem, setCustomItem]       = useState({ name: '', price: '', quantity: '1' });

  useEffect(() => {
    if (!form.pharmacyId) { setCatalog([]); return; }
    const unsubscribe = onValue(ref(db, `pharmacyMedicines/${form.pharmacyId}`), async snap => {
      if (snap.exists()) {
        setCatalog(Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })));
      } else {
        const legacySnap = await get(ref(db, 'medicines'));
        setCatalog(legacySnap.exists()
          ? Object.entries(legacySnap.val()).map(([k, v]) => ({ id: k, ...v })).filter(m => m.pharmacyId === form.pharmacyId)
          : []);
      }
    });
    return () => unsubscribe();
  }, [form.pharmacyId]);

  const catalogResults = catalogSearch.trim()
    ? catalog.filter(m => m.name?.toLowerCase().includes(catalogSearch.trim().toLowerCase())).slice(0, 8)
    : [];

  const addFromCatalog = (med) => {
    const customerPrice = med.customerPrice || med.price || 0;
    const pharmacyPrice = med.pharmacyPrice || parseFloat((customerPrice / (1 + COMMISSION_RATE)).toFixed(2));
    setCartItems(items => {
      const existing = items.find(i => i.catalogId === med.id);
      if (existing) return items.map(i => i.catalogId === med.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...items, {
        id: `cart_${Date.now()}_${items.length}`, catalogId: med.id,
        name: med.name, quantity: 1, customerPrice, pharmacyPrice, price: customerPrice,
        pharmacy: form.pharmacyName, pharmacyId: form.pharmacyId, pharmacyPhone: form.pharmacyPhone, inStock: med.inStock !== false,
      }];
    });
    setCatalogSearch('');
  };

  const updateCartQty = (id, qty) => {
    if (qty <= 0) { setCartItems(items => items.filter(i => i.id !== id)); return; }
    setCartItems(items => items.map(i => i.id === id ? { ...i, quantity: qty } : i));
  };
  const removeCartItem = (id) => setCartItems(items => items.filter(i => i.id !== id));

  const addCustomItem = () => {
    const price = parseFloat(customItem.price);
    const qty = parseInt(customItem.quantity) || 1;
    if (!customItem.name.trim() || !price) { alert('Enter a medicine name and price'); return; }
    const pharmacyPrice = parseFloat((price / (1 + COMMISSION_RATE)).toFixed(2));
    setCartItems(items => [...items, {
      id: `custom_${Date.now()}`, name: customItem.name.trim(), quantity: qty,
      customerPrice: price, pharmacyPrice, price,
      pharmacy: form.pharmacyName, pharmacyId: form.pharmacyId, pharmacyPhone: form.pharmacyPhone,
    }]);
    setCustomItem({ name: '', price: '', quantity: '1' });
    setShowCustomItem(false);
  };

  const phoneOrders = orders
    .filter(o => o.isPhoneOrder === true)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const customerSubtotal = parseFloat(cartItems.reduce((s, i) => s + i.customerPrice * i.quantity, 0).toFixed(2));
  const pharmacySubtotal = parseFloat(cartItems.reduce((s, i) => s + i.pharmacyPrice * i.quantity, 0).toFixed(2));
  const pharmdevCommission = parseFloat((customerSubtotal - pharmacySubtotal).toFixed(2));
  const deliveryFee = parseFloat(form.deliveryFee || 10);
  const total = parseFloat((customerSubtotal + deliveryFee + SERVICE_FEE).toFixed(2));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setCartItems([]);
    setCustomerMatch(null);
    setAddressSuggestion(null);
    setCatalogSearch('');
    setShowCustomItem(false);
  };

  const handleProceed = async () => {
    if (!form.customerName || !form.customerPhone || !form.momoNumber || !form.address) {
      alert('Please fill in customer name, phone, MoMo number and address'); return;
    }
    if (!form.pharmacyId) {
      alert('Please select a pharmacy'); return;
    }
    if (cartItems.length === 0) {
      alert('Add at least one medicine from the catalog, or a custom item'); return;
    }

    setPlacing(true);
    setPollStatus('');
    try {
      const authedFetch = async (path, body) => {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${BACKEND_URL}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Request failed');
        return data;
      };

      const pollUntilSettled = async (reference, maxAttempts = 20, intervalMs = 4000) => {
        for (let i = 0; i < maxAttempts; i++) {
          const data = await authedFetch('/paystack/verify', { reference });
          if (data.success) return true;
          if (data.status === 'failed' || data.status === 'abandoned') return false;
          await new Promise(r => setTimeout(r, intervalMs));
        }
        return false;
      };

      // ── TRIGGER + CONFIRM PAYMENT BEFORE THE ORDER EVER EXISTS ───────────
      // Creating the order first (as this used to) put it in front of the
      // pharmacy — visible, and eligible for rider assignment — the instant
      // it was written, regardless of whether the customer ever approved
      // the MoMo prompt on their phone. Charging first and waiting for
      // Paystack to confirm it settled, exactly like the customer app's own
      // checkout does, means a declined or ignored prompt never produces an
      // order at all.
      setPollStatus('Sending payment prompt…');
      const chargeData = await authedFetch('/paystack/charge', { amount: total, channel: 'mtn', phone: form.momoNumber });

      setPollStatus('Waiting for the customer to approve on their phone…');
      const settled = await pollUntilSettled(chargeData.reference);
      if (!settled) {
        alert('The customer did not approve the payment prompt (or it expired/failed). No order was created — try again once they confirm they can approve it.');
        return;
      }

      // Nearest-rider matching (rider app + backend) reads pharmacyLat/Lng
      // off the order — without it, every order falls back to a hardcoded
      // Accra-center point instead of the pharmacy's real location.
      let pharmacyLat, pharmacyLng;
      try {
        const pharmSnap = await get(ref(db, `pharmacies_map/${form.pharmacyId}`));
        const pharm = pharmSnap.val();
        if (pharm?.latitude && pharm?.longitude) {
          pharmacyLat = pharm.latitude;
          pharmacyLng = pharm.longitude;
        }
      } catch (e) { /* fall back to the rider app's default coordinates */ }

      const orderRef = push(ref(db, 'orders'));
      const orderId  = orderRef.key;

      await set(orderRef, {
        orderId,
        isPhoneOrder:    true,
        takenBy:         auth.currentUser?.email || 'admin',

        // Tied to the caller's real account when we found one, so the
        // order actually appears in their own app — not just a database
        // record only staff can see.
        customerId:      customerMatch?.uid || `phone_${form.customerPhone}`,
        customerName:    form.customerName,
        customerPhone:   form.customerPhone,
        customerEmail:   customerMatch?.email || '',

        momoNumber:      form.momoNumber,
        momoName:        form.momoName,

        items:           cartItems,
        ...(pharmacyLat && pharmacyLng ? { pharmacyLat, pharmacyLng } : {}),

        customerSubtotal,
        pharmacySubtotal,
        pharmdevCommission,
        deliveryFee,
        serviceFee: SERVICE_FEE,
        total,
        subtotal:        customerSubtotal,

        deliveryAddress: form.address,
        ghanaPostGPS:    form.ghanaPostGPS,
        notes:           form.notes,

        paymentMethod:    'mtn',
        paymentStatus:    'paid',
        paystackRef:      chargeData.reference || '',
        paymentReference: chargeData.reference || '',
        status:           'pending',
        createdAt:        new Date().toISOString(),
        updatedAt:        new Date().toISOString(),
      });

      setSuccess({
        orderId,
        orderRef:   orderId.slice(-6).toUpperCase(),
        customer:   form.customerName,
        phone:      form.customerPhone,
        momoNumber: form.momoNumber,
        amount:     total,
        medicines:  cartItems.map(i => `${i.name} x${i.quantity}`).join(', '),
      });

      resetForm();
    } catch (e) {
      alert('Error placing order. Please try again. ' + e.message);
    } finally { setPlacing(false); setPollStatus(''); }
  };

  return (
    <div>
      {/* SUCCESS MODAL */}
      {success && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', textAlign: 'center' }}>
            <p style={{ fontSize: 56, margin: '0 0 12px' }}>✅</p>
            <h3 style={{ fontWeight: 700, fontSize: 20, margin: '0 0 8px', color: '#1D9E75' }}>Order Created!</h3>
            <p style={{ fontSize: 14, color: '#555', margin: '0 0 20px', lineHeight: 1.6 }}>
              Order <strong>#{success.orderRef}</strong> has been placed for <strong>{success.customer}</strong>.
              <br /><br />
              💳 Payment of <strong>GH₵ {success.amount.toFixed(2)}</strong> from <strong>{success.momoNumber}</strong> is confirmed.
              <br /><br />
              The pharmacy can now see the order and begin preparing.
            </p>
            <div style={{ backgroundColor: '#F0FDF4', borderRadius: 10, padding: 14, marginBottom: 20, textAlign: 'left', border: '1px solid #B2DFD0' }}>
              <p style={{ fontWeight: 700, fontSize: 13, color: '#1D6B52', margin: '0 0 8px' }}>📋 Order Summary</p>
              <p style={{ fontSize: 13, color: '#555', margin: '0 0 4px' }}>💊 Medicines: {success.medicines}</p>
              <p style={{ fontSize: 13, color: '#555', margin: 0 }}>💰 Total: GH₵ {success.amount.toFixed(2)}</p>
            </div>
            <button style={{ width: '100%', padding: '13px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              onClick={() => setSuccess(null)}>
              Place Another Order
            </button>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>📞 Phone Order Support</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Take orders from customers who call in — many are elderly customers who need help using the app. Look them up, search real priced medicines, and send a payment prompt.
          </p>
          <p style={{ color: '#1D9E75', fontSize: 13, margin: '4px 0 0', fontWeight: 700 }}>
            📞 Customers call 059 984 8708 for phone orders (055 190 6880 is the general support line)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ padding: '9px 16px', backgroundColor: showForm ? '#1D9E75' : '#F7F8FA', color: showForm ? '#FFF' : '#555', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }} onClick={() => setShowForm(true)}>
            ➕ New Order
          </button>
          <button style={{ padding: '9px 16px', backgroundColor: !showForm ? '#1D9E75' : '#F7F8FA', color: !showForm ? '#FFF' : '#555', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }} onClick={() => setShowForm(false)}>
            📋 History ({phoneOrders.length})
          </button>
        </div>
      </div>

      {showForm ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* LEFT — CUSTOMER INFO */}
          <div>
            <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px', color: '#1D9E75' }}>👤 Step 1 — Find the Customer</h3>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Phone Number *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inp, flex: 1 }} placeholder="e.g. 0244000000" value={form.customerPhone} onChange={e => { upd('customerPhone', e.target.value); setCustomerMatch(null); setAddressSuggestion(null); }} maxLength={10} />
                  <button
                    type="button"
                    style={{ padding: '0 18px', backgroundColor: lookingUp ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: lookingUp ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}
                    onClick={lookupCustomer}
                    disabled={lookingUp}
                  >
                    {lookingUp ? '⏳' : '🔍 Look Up'}
                  </button>
                </div>
                {customerMatch && (
                  <p style={{ fontSize: 12, color: '#15803D', margin: '6px 0 0', fontWeight: 600 }}>
                    ✅ Registered customer found: {customerMatch.name} — this order will appear in their app too
                  </p>
                )}
                {!customerMatch && form.customerPhone && (
                  <p style={{ fontSize: 11, color: '#888', margin: '6px 0 0' }}>
                    No account matched yet — click Look Up, or continue as a guest order
                  </p>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Full Name *</label>
                <input style={inp} placeholder="e.g. Kofi Mensah" value={form.customerName} onChange={e => upd('customerName', e.target.value)} />
              </div>

              <div style={{ backgroundColor: '#EFF6FF', borderRadius: 10, padding: 14, marginBottom: 14, border: '1px solid #BFDBFE' }}>
                <p style={{ fontWeight: 700, fontSize: 13, color: '#1D4ED8', margin: '0 0 10px' }}>📱 MoMo Payment Details</p>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>MTN MoMo Number *</label>
                  <input style={inp} placeholder="e.g. 0244000000" value={form.momoNumber} onChange={e => upd('momoNumber', e.target.value)} maxLength={10} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Registered Name on MoMo</label>
                  <input style={inp} placeholder="Name on MoMo account" value={form.momoName} onChange={e => upd('momoName', e.target.value)} />
                </div>
                <p style={{ fontSize: 11, color: '#3B82F6', margin: '10px 0 0', lineHeight: 1.5 }}>
                  ℹ️ When you click Proceed, a prompt will be sent to this MoMo number. The customer must approve it on their phone to complete payment. Payment will appear as <strong>PharmDev</strong>.
                </p>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Delivery Address *</label>
                <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Full street address for delivery" value={form.address} onChange={e => upd('address', e.target.value)} />
                {addressSuggestion && form.address !== addressSuggestion.address && (
                  <div style={{ marginTop: 8, backgroundColor: '#F7F8FA', borderRadius: 8, padding: 10, border: '1px solid #E5E7EB' }}>
                    <p style={{ fontSize: 11, color: '#888', margin: '0 0 6px' }}>📍 Last delivery address on file:</p>
                    <p style={{ fontSize: 12, color: '#333', margin: '0 0 8px' }}>{addressSuggestion.address}</p>
                    <button type="button" style={{ padding: '6px 12px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={applySuggestedAddress}>
                      Use this address
                    </button>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Ghana Post GPS (optional)</label>
                <input style={inp} placeholder="e.g. AK-123-4567" value={form.ghanaPostGPS} onChange={e => upd('ghanaPostGPS', e.target.value)} />
              </div>
            </div>
          </div>

          {/* RIGHT — ORDER INFO */}
          <div>
            <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 16px', color: '#1D9E75' }}>💊 Step 2 — Add Medicines</h3>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Pharmacy *</label>
                <select style={inp} value={form.pharmacyId} onChange={e => {
                  const p = pharmacies.find(ph => ph.id === e.target.value);
                  upd('pharmacyId', e.target.value);
                  upd('pharmacyName', p?.pharmacyName || '');
                  upd('pharmacyPhone', p?.phone || '');
                  setCartItems([]);
                }}>
                  <option value="">Select pharmacy</option>
                  {pharmacies.filter(p => p.status === 'approved').map(p => (
                    <option key={p.id} value={p.id}>{p.pharmacyName} — {p.region || p.location}</option>
                  ))}
                </select>
              </div>

              {form.pharmacyId && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>
                    Search this pharmacy's medicines
                  </label>
                  <input
                    style={inp}
                    placeholder="Start typing a medicine name..."
                    value={catalogSearch}
                    onChange={e => setCatalogSearch(e.target.value)}
                  />
                  {catalogSearch.trim() && (
                    <div style={{ marginTop: 6, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                      {catalogResults.length === 0 ? (
                        <p style={{ padding: 12, fontSize: 12, color: '#888', margin: 0 }}>No matching medicine in this pharmacy's catalog.</p>
                      ) : catalogResults.map(med => {
                        const price = med.customerPrice || med.price || 0;
                        return (
                          <div key={med.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#FFF' }}>
                            <div>
                              <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 2px', color: '#1A1A1A' }}>{med.name}</p>
                              <p style={{ fontSize: 11, color: med.inStock === false ? '#B91C1C' : '#888', margin: 0 }}>
                                GH₵ {price.toFixed(2)} {med.inStock === false ? '· ⚠️ Out of stock' : ''}
                              </p>
                            </div>
                            <button
                              type="button"
                              style={{ padding: '6px 14px', backgroundColor: med.inStock === false ? '#F1F3F5' : '#1D9E75', color: med.inStock === false ? '#888' : '#FFF', border: 'none', borderRadius: 8, cursor: med.inStock === false ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12 }}
                              onClick={() => addFromCatalog(med)}
                              disabled={med.inStock === false}
                            >
                              + Add
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* CART */}
              {cartItems.length > 0 && (
                <div style={{ marginBottom: 14, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  {cartItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#F7F8FA' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 2px', color: '#1A1A1A' }}>{item.name}</p>
                        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>GH₵ {item.customerPrice.toFixed(2)} each</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button type="button" style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #E5E7EB', backgroundColor: '#FFF', cursor: 'pointer', fontWeight: 700 }} onClick={() => updateCartQty(item.id, item.quantity - 1)}>−</button>
                        <span style={{ fontSize: 13, fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{item.quantity}</span>
                        <button type="button" style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #E5E7EB', backgroundColor: '#FFF', cursor: 'pointer', fontWeight: 700 }} onClick={() => updateCartQty(item.id, item.quantity + 1)}>+</button>
                        <button type="button" style={{ marginLeft: 4, background: 'none', border: 'none', color: '#B91C1C', cursor: 'pointer', fontSize: 14 }} onClick={() => removeCartItem(item.id)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* CUSTOM ITEM FALLBACK */}
              {!showCustomItem ? (
                <button type="button" style={{ padding: '8px 14px', backgroundColor: '#F1F3F5', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 14 }} onClick={() => setShowCustomItem(true)}>
                  + Add an item not in the catalog
                </button>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                  <input style={inp} placeholder="Medicine name" value={customItem.name} onChange={e => setCustomItem(c => ({ ...c, name: e.target.value }))} />
                  <input style={inp} type="number" placeholder="Qty" min="1" value={customItem.quantity} onChange={e => setCustomItem(c => ({ ...c, quantity: e.target.value }))} />
                  <input style={inp} type="number" placeholder="Price" min="0" step="0.01" value={customItem.price} onChange={e => setCustomItem(c => ({ ...c, price: e.target.value }))} />
                  <button type="button" style={{ padding: '11px 14px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 }} onClick={addCustomItem}>Add</button>
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Delivery Fee (GH₵)</label>
                <input style={inp} type="number" placeholder="12.00" value={form.deliveryFee} onChange={e => upd('deliveryFee', e.target.value)} min="0" step="0.50" />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Notes / Special Instructions</label>
                <textarea style={{ ...inp, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Any allergies, dosage preferences, or special delivery instructions" value={form.notes} onChange={e => upd('notes', e.target.value)} />
              </div>
            </div>

            {/* SUMMARY + PROCEED */}
            <div style={{ backgroundColor: '#1D9E75', borderRadius: 12, padding: 20, color: '#FFF' }}>
              <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 14px' }}>📋 Order Summary — Step 3</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Customer</span>
                <span style={{ fontWeight: 600 }}>{form.customerName || '—'}{customerMatch ? ' (registered)' : ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>MoMo Number</span>
                <span style={{ fontWeight: 600 }}>{form.momoNumber || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Pharmacy</span>
                <span style={{ fontWeight: 600 }}>{form.pharmacyName || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Medicines</span>
                <span style={{ fontWeight: 600 }}>{cartItems.length} item{cartItems.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Medicines Subtotal</span>
                <span style={{ fontWeight: 600 }}>GH₵ {customerSubtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Service Fee</span>
                <span style={{ fontWeight: 600 }}>GH₵ {SERVICE_FEE.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 10, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#A8EDCF', fontSize: 13 }}>Total (Medicines + Delivery + Service)</span>
                <span style={{ fontSize: 20, fontWeight: 800 }}>GH₵ {total.toFixed(2)}</span>
              </div>

              <button
                style={{ width: '100%', marginTop: 16, padding: '14px', backgroundColor: placing ? 'rgba(255,255,255,0.4)' : '#FFF', color: placing ? '#FFF' : '#1D9E75', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: placing ? 'not-allowed' : 'pointer' }}
                onClick={handleProceed}
                disabled={placing}
              >
                {placing ? `⏳ ${pollStatus || 'Placing order...'}` : '📱 Proceed — Send MoMo Prompt to Customer'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* PHONE ORDER HISTORY */
        <div>
          <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>All orders taken via phone support — {phoneOrders.length} total</p>
          {phoneOrders.length === 0 ? (
            <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', textAlign: 'center', padding: 60 }}>
              <p style={{ fontSize: 40, marginBottom: 12 }}>📞</p>
              <p style={{ fontWeight: 600, color: '#333', margin: '0 0 6px' }}>No phone orders yet</p>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Phone orders placed through this module will appear here</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
              {phoneOrders.map((order, i) => (
                <div key={order.id} style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', borderTop: `4px solid ${order.status === 'delivered' ? '#1D9E75' : order.status === 'pending' ? '#F59E0B' : '#3B82F6'}` }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F7F8FA' }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 2px', color: '#1D9E75' }}>
                        📞 #{(order.orderId || order.id).slice(-6).toUpperCase()}
                      </p>
                      <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{new Date(order.createdAt).toLocaleString()}</p>
                    </div>
                    <span style={{
                      backgroundColor: order.status === 'delivered' ? '#DCFCE7' : order.status === 'pending' ? '#FEF3C7' : '#EFF6FF',
                      color: order.status === 'delivered' ? '#15803D' : order.status === 'pending' ? '#92400E' : '#1D4ED8',
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>
                      {order.status}
                    </span>
                  </div>
                  <div style={{ padding: 16 }}>
                    {[
                      ['👤 Customer',   order.customerName],
                      ['📞 Phone',      order.customerPhone],
                      ['📱 MoMo',       order.momoNumber],
                      ['💊 Medicines',  order.items?.map(i => i.name).join(', ')],
                      ['📍 Address',    order.deliveryAddress],
                      ['💰 Fee',        `GH₵ ${order.total?.toFixed(2)}`],
                      ['👤 Taken by',   order.takenBy],
                      ['💳 Payment',    order.paymentStatus],
                    ].map(([l, v]) => v && (
                      <div key={l} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#888', minWidth: 100 }}>{l}</span>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#1A1A1A', flex: 1 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
